import { AnyAction, combineReducers, Reducer } from "redux";
import produce from "immer";

import { createInstanceMapReducer, EMPTY_ARRAY, EMPTY_OBJECT } from "@multiweb/reactUtils";
import { ParametricReducer, ReduxCompatibleValue } from "@multiweb/types/react";
import { INVALID_CONTROL_VALUE_LOC_KEY } from "./locKeys";
import {
    ControlReduxRawValue,
    ControlsInstance,
    ControlsInstances,
    ControlsValueInstantValidationMode,
    ErrorsReducerExtraParams,
    IClearControlsValueAction,
    IClearValidationErrorsAction,
    IControlsMappedAction,
    IControlsState,
    IControlsValueChangedAction,
    IInitControlsValueAction,
    ISetValidationDescriptionsAction,
    IValidateAction,
} from "./types";
import {
    areErrorsEqual,
    ExplicitValidation,
    IExplicitValidationError,
    IExplicitValidationFnDescription,
    IInstantValidationFnDescription,
    InstantValidation,
} from "./validation";

/**
 * <p> Proměnná je použita jako inicializace stavu pro redux.
 * <p> Pozn. Při vytvoření libovolného reduceru je jeho výchozí stav nastaven na `undefined` a proto chceme námi přiřazenou výchozí hodnotu odlišit. Pro označení prázdné hodnoty je použit typ `null`.
 * <p> Pokud bychom si i přes to chtěli vynutit výchozí hodnotu stavu o typu `undefined`, museli bychom v reduceru transformovat stav na cokoli jiného mimo `undefined`, tzn. např. na `null`.
 * V opačném případě bychom obdrželi chybovou hlášku.
 */
const INITIAL_CONTROLS_VALUE: ControlReduxRawValue = null;
const INITIAL_CONTROLS_COUNTER_VALUE = 0;

const rawValue: ParametricReducer<ReduxCompatibleValue, ReadonlyArray<IInstantValidationFnDescription>> = (state = INITIAL_CONTROLS_VALUE, action, instantValidationFnDescriptions) => {
    if (action instanceof IInitControlsValueAction) {
        return action.payload.rawValue;
    } else if (action instanceof IControlsValueChangedAction) {
        const { rawValue, validationMode } = action.payload;
        switch (validationMode) {
            case ControlsValueInstantValidationMode.initialValue:
            case ControlsValueInstantValidationMode.allowInvalid:
                return rawValue;
            case ControlsValueInstantValidationMode.userEdit: {
                const allowNewValue = InstantValidation.isValueValid(rawValue, instantValidationFnDescriptions);
                return allowNewValue ? (rawValue ?? null) : state;
            }
            default:
                throw new Error(`[controls] Unexpected validationMode: ${validationMode}`);
        }
    } else {
        return state;
    }
};

const initialRawValue: Reducer<ReduxCompatibleValue> = (state = INITIAL_CONTROLS_VALUE, action) => {
    if (action instanceof IControlsValueChangedAction) {
        const { rawValue, validationMode } = action.payload;
        switch (validationMode) {
            case ControlsValueInstantValidationMode.initialValue:
                return rawValue;
            case ControlsValueInstantValidationMode.userEdit:
            case ControlsValueInstantValidationMode.allowInvalid:
                return state;
            default:
                throw new Error(`[controls] Unexpected validationMode: ${validationMode}`);
        }
    } else {
        return state;
    }
};

const counter: Reducer<number> = (state = INITIAL_CONTROLS_COUNTER_VALUE, action) => {
    if (action instanceof IInitControlsValueAction) {
        return ++state;
    } else if (action instanceof IClearControlsValueAction) {
        return --state;
    } else {
        return state;
    }
};

const instantValidationFnDescriptions: Reducer<ReadonlyArray<IInstantValidationFnDescription>> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof IInitControlsValueAction) {
        return EMPTY_ARRAY;
    } else if (action instanceof ISetValidationDescriptionsAction) {
        return action.payload.instantValidationFnDescriptions;
    } else {
        return state;
    }
};

const explicitValidationFnDescriptions: Reducer<ReadonlyArray<IExplicitValidationFnDescription>> = (state = EMPTY_ARRAY, action) => {
    if (action instanceof IInitControlsValueAction) {
        return EMPTY_ARRAY;
    } else if (action instanceof ISetValidationDescriptionsAction) {
        return action.payload.explicitValidationFnDescriptions;
    } else {
        return state;
    }
};

const errors: ParametricReducer<ReadonlyArray<IExplicitValidationError>, ErrorsReducerExtraParams> = (
    state = EMPTY_ARRAY as ReadonlyArray<IExplicitValidationError>,
    action: AnyAction,
    {
        rawValue,
        initialRawValue,
        instantValidationFnDescriptions,
        explicitValidationFnDescriptions,
    },
) => {
    if (action instanceof IValidateAction) {
        if (rawValue === initialRawValue) {
            // Pokud hodnotu uživatel nezměnil, pak ji chceme považovat za platnou, i kdyby platná nebyla.
            // Usecase:
            //   - Uživatel načte data do formuláře.
            //   - Z nějakého důvodu jsou ale již data ze serveru neplatná. (Např. proto, že definice formuláře se změnila.)
            //   - Uživatel změní nějaké attributy, ale zrovna ten s neplatnou hodnotou ze serveru nezmění.
            //   - Je žádoucí, aby to šlo uživateli uložit.
            return EMPTY_ARRAY;
        } else {
            const newExplicitErrors = ExplicitValidation.validateValue(rawValue, explicitValidationFnDescriptions);

            // Díky ControlsValueInstantValidationMode.initialValue může být `value` nevalidní i s ohledem na InstantValidation.
            const isInstantValidationOK = InstantValidation.isValueValid(rawValue, instantValidationFnDescriptions);
            const newErrors = isInstantValidationOK ? newExplicitErrors : [ExplicitValidation.createValidationError(INVALID_CONTROL_VALUE_LOC_KEY), ...newExplicitErrors];

            return areErrorsEqual(state, newErrors) ? state : newErrors;
        }
    } else if (action instanceof IClearValidationErrorsAction) {
        return EMPTY_ARRAY;
    } else {
        return state;
    }
};

const controlsInstanceReducer: Reducer<ControlsInstance> = (state: ControlsInstance | undefined = EMPTY_OBJECT as ControlsInstance, action) => {
    const newInstantValidationFnDescriptions = instantValidationFnDescriptions(state.instantValidationFnDescriptions, action);
    const newExplicitValidationFnDescriptions = explicitValidationFnDescriptions(state.explicitValidationFnDescriptions, action);
    const newCounter = counter(state.counter, action);

    // Při opětovném inicializování se hodnota controlu již nebude měnit.
    // Změnu hodnoty je nutné provést pomocí akce {@link IControlsValueChangedAction}.
    const newRawValue = action instanceof IInitControlsValueAction && newCounter > 1
        ? state?.rawValue
        : rawValue(state.rawValue, action, newInstantValidationFnDescriptions);
    const newInitialRawValue = initialRawValue(state.initialRawValue, action);

    const errorsExtraParams = {
        rawValue: newRawValue,
        initialRawValue: newInitialRawValue,
        instantValidationFnDescriptions: newInstantValidationFnDescriptions,
        explicitValidationFnDescriptions: newExplicitValidationFnDescriptions,
    };

    return produce(state, (draft: any) => {
        draft.counter = newCounter;
        draft.instantValidationFnDescriptions = newInstantValidationFnDescriptions;
        draft.explicitValidationFnDescriptions = newExplicitValidationFnDescriptions;
        draft.rawValue = newRawValue;
        draft.initialRawValue = newInitialRawValue;
        draft.errors = errors(state.errors, action, errorsExtraParams);
    });
};

const instanceKeyResolver = (action: AnyAction) => {
    return action instanceof IControlsMappedAction ? action.meta.controlsKey : undefined;
};

const instanceControlsValueMapReducer = createInstanceMapReducer(controlsInstanceReducer, instanceKeyResolver);

const controlsInstances: Reducer<ControlsInstances> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IClearControlsValueAction) {
        return produce(state, (draft: any) => {
            const controlsInstance = controlsInstanceReducer(draft[action.meta.controlsKey], action);
            draft[action.meta.controlsKey] = controlsInstance;

            if (controlsInstance.counter <= 0) {
                delete draft[action.meta.controlsKey];
            }
        });
    } else {
        return instanceControlsValueMapReducer(state, action);
    }
};

export const reducer: Reducer<IControlsState> = combineReducers({
    controlsInstances,
});
